import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faFileInvoice, faSearch } from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, setDoc, Timestamp, where } from 'firebase/firestore';
import { getFirestore } from '@firebase/firestore';
import { app } from '../firebase.js';
import defaulProfPic from '../Images/defprofile.png';
import MaskedPhone from '../Components/MaskedPhone';
import { logAdminActivity } from '../utils/activityLogger';
import { buildFirstPaymentInvoiceEmail } from '../utils/emailTemplates';

const EMAIL_API_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email';
const SMS_API_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms';
const INVOICE_BASE_URL = 'https://live.ubunifucollege.com/view-invoice?';
const STUDENT_DASHBOARD_URL = 'https://live.ubunifucollege.com/login';
const FEE_PAYMENT_PAYBILL = '4052081';
const DEFAULT_USD_TO_KES_RATE = 130;

const getStudentId = (student) => student?.userId || student?.id || '';
const getStudentDocId = (student) => student?.id || student?.userId || '';
const getApplicationRef = (student) => (
  [student?.prospectAdmissionNumber, student?.applicationRef, student?.admissionNumber]
    .map((value) => String(value || '').trim())
    .find(Boolean) || ''
);

const getDateObject = (value) => {
  if (!value) return null;
  const dateObj = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return null;
  return dateObj;
};

const formatDateInputValue = (value) => {
  const dateObj = getDateObject(value);
  if (!dateObj) return '';

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDueDate = () => {
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + 2);
  return formatDateInputValue(dueDate);
};

const formatDateTime = (value) => {
  const dateObj = getDateObject(value);
  if (!dateObj) return 'Not set';

  return dateObj.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatDateOnly = (value) => {
  const dateObj = getDateObject(value);
  if (!dateObj) return 'Not set';

  return dateObj.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatCurrency = (value, currency = 'KES') => {
  const amount = Number(value || 0);
  const safeCurrency = currency === 'USD' ? 'USD' : 'KES';
  const maximumFractionDigits = safeCurrency === 'USD' ? 2 : 0;

  if (!Number.isFinite(amount)) return `${safeCurrency} 0`;

  return `${safeCurrency} ${amount.toLocaleString(safeCurrency === 'USD' ? 'en-US' : 'en-KE', {
    minimumFractionDigits: safeCurrency === 'USD' ? 2 : 0,
    maximumFractionDigits
  })}`;
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const parseMoneyValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const sanitizedValue = String(value || '').replace(/[^\d.-]/g, '');
  const amount = Number(sanitizedValue);
  return Number.isFinite(amount) ? amount : 0;
};

const isKenyanSmsPhone = (phoneNumber = '') => /^\+254[17]\d{8}$/.test(phoneNumber);

const getInvoiceCurrency = (student = {}) => {
  const applicant = student || {};
  const savedCurrency = String(applicant.currency || '').trim().toUpperCase();
  const nationality = normalizeText(applicant.nationality);

  if (savedCurrency === 'USD' || (nationality && nationality !== 'kenya')) {
    return 'USD';
  }

  return 'KES';
};

const convertKesToInvoiceAmount = (amountKes, currency, usdToKesRate) => {
  const numericAmount = parseMoneyValue(amountKes);
  const numericRate = parseMoneyValue(usdToKesRate) || DEFAULT_USD_TO_KES_RATE;

  if (currency === 'USD') {
    return Number((numericAmount / numericRate).toFixed(2));
  }

  return Math.round(numericAmount);
};

const generateInvoiceNumber = () => Math.floor(1000000 + Math.random() * 9000000);

const getInvoiceStatusMeta = (student) => {
  if (student?.firstPaymentInvoiceStatus === 'sent') {
    return { label: 'Invoice sent', className: 'interviewStatusPill interviewStatusPill--ready' };
  }

  if (student?.firstPaymentInvoiceStatus === 'failed') {
    return { label: 'Send failed', className: 'interviewStatusPill interviewStatusPill--ended' };
  }

  return { label: 'Ready to invoice', className: 'interviewStatusPill interviewStatusPill--idle' };
};

const postAuthorizedJson = async ({ url, token, payload }) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.error || data?.message || `Request failed with status ${response.status}`);
  }

  return data;
};

const buildFirstPaymentSms = ({ student, amount, currency, dueDate, invoiceNumber }) => {
  const studentName = student?.bio || 'there';
  const applicationRef = getApplicationRef(student);
  const invoiceUrl = `${INVOICE_BASE_URL}${invoiceNumber}`;
  const payText = currency === 'USD'
    ? `Pay from your student account: ${STUDENT_DASHBOARD_URL}.`
    : `Pay from your student account ${STUDENT_DASHBOARD_URL} or use Paybill ${FEE_PAYMENT_PAYBILL}, Account ${applicationRef || 'Application Ref pending'}.`;

  return `Hi ${studentName}, your Ubunifu College first fee invoice of ${formatCurrency(amount, currency)} is ready. Due ${formatDateOnly(dueDate)}. ${payText} Invoice: ${invoiceUrl}. - Ubunifu`;
};

function InvoicingComp() {
  const db = getFirestore(app);
  const auth = getAuth();
  const navigate = useNavigate();

  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceDueDate, setInvoiceDueDate] = useState(getDefaultDueDate);
  const [usdToKesRate, setUsdToKesRate] = useState(DEFAULT_USD_TO_KES_RATE);
  const [sendingInvoice, setSendingInvoice] = useState(false);

  const fetchInvoicingApplicants = useCallback(async () => {
    setLoading(true);

    try {
      const [usersSnapshot, endedCallsSnapshot, coursesSnapshot, currencySettingsSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'users'),
          where('interviewStatus', '==', 'completed')
        )),
        getDocs(query(
          collection(db, 'interviewCalls'),
          where('status', '==', 'ended')
        )),
        getDocs(collection(db, 'courses')),
        getDoc(doc(db, 'Settings', 'currency'))
      ]);

      const activeUsdToKesRate = parseMoneyValue(currencySettingsSnap.data()?.usdToKesRate) || DEFAULT_USD_TO_KES_RATE;
      setUsdToKesRate(activeUsdToKesRate);

      const courseFeeMap = new Map();
      coursesSnapshot.forEach((courseDoc) => {
        const courseData = courseDoc.data();
        const courseName = normalizeText(courseData.mcourseName);

        if (!courseName) {
          return;
        }

        courseFeeMap.set(courseName, {
          feeDeposit: parseMoneyValue(courseData.feeDeposit),
          totalFee: parseMoneyValue(courseData.mcoursePrice),
          feeInstallments: parseMoneyValue(courseData.feeInstallments)
        });
      });

      const applicantsMap = new Map();
      const addApplicant = (userDoc, callData = {}) => {
        const data = userDoc.data();
        const status = normalizeText(data.userStatus);
        const courseFees = courseFeeMap.get(normalizeText(data.course)) || {};
        const invoiceCurrency = getInvoiceCurrency(data);
        const courseFeeDepositKes = courseFees.feeDeposit || 0;
        const courseTotalFeeKes = courseFees.totalFee || 0;
        const courseFeeInstallmentsKes = courseFees.feeInstallments || 0;

        if (status && status !== 'inactive') {
          return;
        }

        applicantsMap.set(userDoc.id, {
          id: userDoc.id,
          ...data,
          userId: data.userId || userDoc.id,
          interviewCompletedAt: data.interviewCompletedAt || callData.endedAt || data.interviewScheduledFor,
          invoiceCurrency,
          usdToKesRate: activeUsdToKesRate,
          courseFeeDeposit: convertKesToInvoiceAmount(courseFeeDepositKes, invoiceCurrency, activeUsdToKesRate),
          courseTotalFee: convertKesToInvoiceAmount(courseTotalFeeKes, invoiceCurrency, activeUsdToKesRate),
          courseFeeInstallments: convertKesToInvoiceAmount(courseFeeInstallmentsKes, invoiceCurrency, activeUsdToKesRate),
          courseFeeDepositKes,
          courseTotalFeeKes,
          courseFeeInstallmentsKes
        });
      };

      usersSnapshot.forEach((userDoc) => {
        addApplicant(userDoc);
      });

      await Promise.all(endedCallsSnapshot.docs.map(async (callDoc) => {
        if (applicantsMap.has(callDoc.id)) {
          const existingApplicant = applicantsMap.get(callDoc.id);
          if (!existingApplicant.interviewCompletedAt && callDoc.data().endedAt) {
            applicantsMap.set(callDoc.id, {
              ...existingApplicant,
              interviewCompletedAt: callDoc.data().endedAt
            });
          }
          return;
        }

        const userSnap = await getDoc(doc(db, 'users', callDoc.id));
        if (userSnap.exists()) {
          addApplicant(userSnap, callDoc.data());
        }
      }));

      const interviewedApplicants = Array.from(applicantsMap.values());

      interviewedApplicants.sort((firstApplicant, secondApplicant) => {
        const firstDate = getDateObject(
          firstApplicant.interviewCompletedAt ||
          firstApplicant.interviewScheduledFor ||
          firstApplicant.admissionFeePaidAt ||
          firstApplicant.appDate
        )?.getTime() || 0;
        const secondDate = getDateObject(
          secondApplicant.interviewCompletedAt ||
          secondApplicant.interviewScheduledFor ||
          secondApplicant.admissionFeePaidAt ||
          secondApplicant.appDate
        )?.getTime() || 0;

        return secondDate - firstDate;
      });

      setApplicants(interviewedApplicants);
    } catch (error) {
      console.error('Error loading invoicing applicants:', error);
      swal('Error', 'Failed to load interviewed applicants.', 'error');
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchInvoicingApplicants();
  }, [fetchInvoicingApplicants]);

  const filteredApplicants = useMemo(() => {
    const normalizedSearch = normalizeText(searchTerm);

    if (!normalizedSearch) {
      return applicants;
    }

    return applicants.filter((student) => {
      const haystack = [
        student.bio,
        student.userEmail,
        student.phoneNumber,
        student.course,
        student.cohort,
        student.invoiceCurrency,
        student.firstPaymentInvoiceCurrency,
        student.prospectAdmissionNumber,
        student.firstPaymentInvoiceNumber,
        student.admissionFeePaymentReference
      ].map(normalizeText).join(' ');

      return haystack.includes(normalizedSearch);
    });
  }, [applicants, searchTerm]);

  const readyToInvoiceCount = useMemo(() => (
    applicants.filter((student) => student.firstPaymentInvoiceStatus !== 'sent').length
  ), [applicants]);

  const sentInvoiceCount = useMemo(() => (
    applicants.filter((student) => student.firstPaymentInvoiceStatus === 'sent').length
  ), [applicants]);

  const totalFirstInvoiceAmount = useMemo(() => (
    applicants.reduce((totals, student) => {
      const currency = student.firstPaymentInvoiceCurrency || student.invoiceCurrency || getInvoiceCurrency(student);
      const amount = parseMoneyValue(student.firstPaymentInvoiceAmount);

      if (currency === 'USD') {
        return { ...totals, USD: totals.USD + amount };
      }

      return { ...totals, KES: totals.KES + amount };
    }, { KES: 0, USD: 0 })
  ), [applicants]);

  const totalFirstInvoiceLabel = useMemo(() => {
    const totals = [];

    if (totalFirstInvoiceAmount.KES) {
      totals.push(formatCurrency(totalFirstInvoiceAmount.KES, 'KES'));
    }

    if (totalFirstInvoiceAmount.USD) {
      totals.push(formatCurrency(totalFirstInvoiceAmount.USD, 'USD'));
    }

    return totals.length ? totals.join(' / ') : formatCurrency(0, 'KES');
  }, [totalFirstInvoiceAmount]);

  const openInvoiceModal = useCallback((student) => {
    const courseDeposit = parseMoneyValue(student.courseFeeDeposit);
    const savedInvoiceAmount = parseMoneyValue(student.firstPaymentInvoiceAmount);

    setSelectedApplicant(student);
    setInvoiceAmount(savedInvoiceAmount || courseDeposit ? String(savedInvoiceAmount || courseDeposit) : '');
    setInvoiceDueDate(formatDateInputValue(student.firstPaymentInvoiceDueDate) || getDefaultDueDate());
    setShowInvoiceModal(true);
  }, []);

  const closeInvoiceModal = useCallback(() => {
    if (sendingInvoice) return;

    setShowInvoiceModal(false);
    setSelectedApplicant(null);
    setInvoiceAmount('');
    setInvoiceDueDate(getDefaultDueDate());
  }, [sendingInvoice]);

  const viewStudent = useCallback((student) => {
    const studentId = getStudentId(student);

    logAdminActivity({
      action: 'View Student',
      details: `Opened invoicing candidate: ${student?.bio || 'student'}`,
      meta: { page: 'Invoicing', studentId, studentName: student?.bio || '' }
    });

    navigate('/view-student', { state: { myData: studentId } });
  }, [navigate]);

  const handleSendInvoice = useCallback(async () => {
    const studentId = getStudentId(selectedApplicant);
    const studentDocId = getStudentDocId(selectedApplicant);
    const numericAmount = parseMoneyValue(invoiceAmount);
    const invoiceCurrency = selectedApplicant?.invoiceCurrency || getInvoiceCurrency(selectedApplicant);
    const activeUsdToKesRate = parseMoneyValue(selectedApplicant?.usdToKesRate || usdToKesRate) || DEFAULT_USD_TO_KES_RATE;
    const amountInKes = invoiceCurrency === 'USD'
      ? Number((numericAmount * activeUsdToKesRate).toFixed(2))
      : numericAmount;

    if (!selectedApplicant || !studentId || !studentDocId) {
      swal('Error', 'Please choose a student before sending an invoice.', 'error');
      return;
    }

    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      swal('Error', 'Please enter a valid first payment amount.', 'error');
      return;
    }

    if (!invoiceDueDate) {
      swal('Error', 'Please select an invoice due date.', 'error');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      swal('Error', 'Please log in again before sending this invoice.', 'error');
      return;
    }

    const dueDateObject = new Date(`${invoiceDueDate}T23:59:00`);
    if (Number.isNaN(dueDateObject.getTime())) {
      swal('Error', 'Please select a valid due date.', 'error');
      return;
    }

    setSendingInvoice(true);

    const invoiceNumber = generateInvoiceNumber();
    const invoiceUrl = `${INVOICE_BASE_URL}${invoiceNumber}`;
    const invoiceRef = doc(collection(db, 'Invoices'));
    const dueDateTimestamp = Timestamp.fromDate(dueDateObject);
    const now = Timestamp.now();
    const applicationRef = getApplicationRef(selectedApplicant);

    try {
      await setDoc(invoiceRef, {
        studentId,
        amount: numericAmount,
        dueDate: dueDateTimestamp,
        createdBy: currentUser.uid,
        createdByEmail: currentUser.email || '',
        timeCreated: now,
        paymentId: invoiceRef.id,
        invoiceNumber,
        invoiceType: 'first_fee_payment',
        source: 'post_interview_invoicing',
        studentName: selectedApplicant.bio || '',
        studentEmail: selectedApplicant.userEmail || '',
        phoneNumber: selectedApplicant.phoneNumber || '',
        course: selectedApplicant.course || '',
        cohort: selectedApplicant.cohort || '',
        prospectAdmissionNumber: selectedApplicant.prospectAdmissionNumber || '',
        currency: invoiceCurrency,
        amountInKes,
        exchangeRateUsdToKes: activeUsdToKesRate,
        paymentInstructionType: invoiceCurrency === 'USD' ? 'student_account_only' : 'student_account_or_paybill',
        paybill: invoiceCurrency === 'USD' ? '' : FEE_PAYMENT_PAYBILL,
        accountNumber: invoiceCurrency === 'USD' ? '' : applicationRef,
        studentPaymentUrl: STUDENT_DASHBOARD_URL,
        courseFeeDeposit: parseMoneyValue(selectedApplicant.courseFeeDeposit),
        courseFeeDepositKes: parseMoneyValue(selectedApplicant.courseFeeDepositKes),
        courseTotalFee: parseMoneyValue(selectedApplicant.courseTotalFee),
        courseTotalFeeKes: parseMoneyValue(selectedApplicant.courseTotalFeeKes),
        courseFeeInstallments: parseMoneyValue(selectedApplicant.courseFeeInstallments),
        courseFeeInstallmentsKes: parseMoneyValue(selectedApplicant.courseFeeInstallmentsKes)
      });

      const token = await currentUser.getIdToken();
      const emailHtml = buildFirstPaymentInvoiceEmail({
        studentName: selectedApplicant.bio,
        course: selectedApplicant.course,
        amount: numericAmount,
        currency: invoiceCurrency,
        dueDate: dueDateObject,
        invoiceNumber,
        paybill: FEE_PAYMENT_PAYBILL,
        accountNumber: applicationRef
      });

      let emailStatus = selectedApplicant.userEmail ? 'pending' : 'skipped_no_email';
      let emailError = '';
      let smsStatus = isKenyanSmsPhone(selectedApplicant.phoneNumber) ? 'pending' : 'skipped_non_kenyan';
      let smsError = '';

      if (selectedApplicant.userEmail) {
        try {
          await postAuthorizedJson({
            url: EMAIL_API_URL,
            token,
            payload: {
              email: selectedApplicant.userEmail,
              subject: `First Payment Invoice #${invoiceNumber}`,
              message: emailHtml
            }
          });
          emailStatus = 'sent';
        } catch (error) {
          console.error('Error sending first payment invoice email:', error);
          emailStatus = 'failed';
          emailError = error.message || 'Failed to send email';
        }
      }

      if (isKenyanSmsPhone(selectedApplicant.phoneNumber)) {
        try {
          await postAuthorizedJson({
            url: SMS_API_URL,
            token,
            payload: {
              phoneNumber: selectedApplicant.phoneNumber,
              message: buildFirstPaymentSms({
                student: selectedApplicant,
                amount: numericAmount,
                currency: invoiceCurrency,
                dueDate: dueDateObject,
                invoiceNumber
              })
            }
          });
          smsStatus = 'sent';
        } catch (error) {
          console.error('Error sending first payment invoice SMS:', error);
          smsStatus = 'failed';
          smsError = error.message || 'Failed to send SMS';
        }
      }

      const messageSent = emailStatus === 'sent' || smsStatus === 'sent';
      const studentUpdates = {
        applicationStage: messageSent ? 'First Fee Invoice Sent' : 'First Fee Invoice Message Failed',
        firstPaymentInvoiceStatus: messageSent ? 'sent' : 'failed',
        firstPaymentInvoiceId: invoiceRef.id,
        firstPaymentInvoiceNumber: invoiceNumber,
        firstPaymentInvoiceAmount: numericAmount,
        firstPaymentInvoiceCurrency: invoiceCurrency,
        firstPaymentInvoiceAmountInKes: amountInKes,
        firstPaymentInvoiceExchangeRateUsdToKes: activeUsdToKesRate,
        firstPaymentInvoicePaymentInstructionType: invoiceCurrency === 'USD' ? 'student_account_only' : 'student_account_or_paybill',
        firstPaymentInvoicePaybill: invoiceCurrency === 'USD' ? '' : FEE_PAYMENT_PAYBILL,
        firstPaymentInvoiceAccountNumber: invoiceCurrency === 'USD' ? '' : applicationRef,
        firstPaymentInvoiceStudentPaymentUrl: STUDENT_DASHBOARD_URL,
        firstPaymentInvoiceCourseDeposit: parseMoneyValue(selectedApplicant.courseFeeDeposit),
        firstPaymentInvoiceCourseDepositKes: parseMoneyValue(selectedApplicant.courseFeeDepositKes),
        firstPaymentInvoiceDueDate: dueDateTimestamp,
        firstPaymentInvoiceUrl: invoiceUrl,
        firstPaymentInvoiceEmailStatus: emailStatus,
        firstPaymentInvoiceEmailError: emailError,
        firstPaymentInvoiceSmsStatus: smsStatus,
        firstPaymentInvoiceSmsError: smsError,
        firstPaymentInvoiceSentAt: messageSent ? now : null,
        updatedAt: now
      };

      await Promise.all([
        setDoc(doc(db, 'Invoices', invoiceRef.id), {
          currency: invoiceCurrency,
          amountInKes,
          exchangeRateUsdToKes: activeUsdToKesRate,
          paymentInstructionType: invoiceCurrency === 'USD' ? 'student_account_only' : 'student_account_or_paybill',
          paybill: invoiceCurrency === 'USD' ? '' : FEE_PAYMENT_PAYBILL,
          accountNumber: invoiceCurrency === 'USD' ? '' : applicationRef,
          studentPaymentUrl: STUDENT_DASHBOARD_URL,
          emailStatus,
          emailError,
          smsStatus,
          smsError,
          invoiceUrl,
          messageSentAt: messageSent ? now : null,
          updatedAt: now
        }, { merge: true }),
        setDoc(doc(db, 'users', studentDocId), studentUpdates, { merge: true })
      ]);

      setApplicants((prevApplicants) => prevApplicants.map((student) => (
        getStudentId(student) === studentId
          ? { ...student, ...studentUpdates }
          : student
      )));

      logAdminActivity({
        action: 'Send First Payment Invoice',
        details: `Sent first payment invoice #${invoiceNumber} to ${selectedApplicant.bio || 'student'} (${formatCurrency(numericAmount, invoiceCurrency)})`,
        meta: {
          page: 'Invoicing',
          studentId,
          studentName: selectedApplicant.bio || '',
          invoiceNumber,
          amount: numericAmount,
          currency: invoiceCurrency,
          amountInKes,
          emailStatus,
          smsStatus
        }
      });

      closeInvoiceModal();

      if (emailStatus === 'sent' && (smsStatus === 'sent' || smsStatus === 'skipped_non_kenyan')) {
        swal('Invoice sent', 'The first payment invoice has been sent and recorded.', 'success');
      } else if (messageSent) {
        swal('Invoice partially sent', 'The invoice was recorded, but one message channel failed. Check the student record for details.', 'warning');
      } else {
        swal('Message failed', 'The invoice was recorded, but email and SMS did not send. Please check the API logs before retrying.', 'error');
      }
    } catch (error) {
      console.error('Error creating first payment invoice:', error);
      swal('Error', 'Failed to create the first payment invoice. Please try again.', 'error');
    } finally {
      setSendingInvoice(false);
    }
  }, [
    auth,
    closeInvoiceModal,
    db,
    invoiceAmount,
    invoiceDueDate,
    selectedApplicant,
    usdToKesRate
  ]);

  return (
    <div className="clientsComp applicationsPage interviewsPage invoicingPage">
      <div className="appsHeader">
        <div>
          <p className="appsKicker">Admissions Finance</p>
          <h4>Invoicing</h4>
          <p className="interviewCardsSubtext">Students appear here after their admissions interview is completed.</p>
        </div>
        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>Interviewed</span>
            <strong>{applicants.length}</strong>
          </div>
          <div className="appsStatChip">
            <span>Ready</span>
            <strong>{readyToInvoiceCount}</strong>
          </div>
          <div className="appsStatChip">
            <span>Invoices sent</span>
            <strong>{sentInvoiceCount}</strong>
          </div>
          <div className="appsStatChip">
            <span>Total invoiced</span>
            <strong>{totalFirstInvoiceLabel}</strong>
          </div>
        </div>
      </div>

      <div className="appsFiltersCard interviewsFiltersCard">
        <div className="appsSearch input-group">
          <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
          <Form.Control
            type="text"
            placeholder="Search name, admission ref, invoice no, course, phone..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <Button variant="outline-dark" className="interviewsRefreshBtn" onClick={fetchInvoicingApplicants}>
          Refresh
        </Button>
      </div>

      <div className="appsTableCard interviewCardsSection">
        <div className="appsTableHeader interviewCardsHeader">
          <div>
            <h6>First Payment Invoice Queue</h6>
            <p className="interviewCardsSubtext">Interviewed applicants who are ready to receive the first fee payment invoice.</p>
          </div>
          <span>{filteredApplicants.length} results</span>
        </div>

        {loading ? (
          <div className="interviewsLoadingState">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : filteredApplicants.length === 0 ? (
          <div className="appsEmptyState interviewCardsEmptyState">
            No completed interviews are waiting for invoicing right now.
          </div>
        ) : (
          <div className="interviewCardsGrid">
            {filteredApplicants.map((student) => {
              const studentId = getStudentId(student);
              const statusMeta = getInvoiceStatusMeta(student);
              const invoiceCurrency = student.firstPaymentInvoiceCurrency || student.invoiceCurrency || getInvoiceCurrency(student);

              return (
                <article key={student.id} className="interviewApplicantCard invoicingApplicantCard">
                  <div className="interviewApplicantCardTop">
                    <div className="appsStudentCell">
                      <div className="appDefProfPic">
                        <img src={student.profilePic ? student.profilePic : defaulProfPic} alt="profile" />
                      </div>
                      <div>
                        <p className="appsStudentName">{student.bio || 'Unnamed applicant'}</p>
                        <span className="appsStudentMeta">{student.userEmail || 'Email not set'}</span>
                      </div>
                    </div>
                    <span className={statusMeta.className}>
                      {statusMeta.label}
                    </span>
                  </div>

                  <div className="interviewApplicantTags">
                    <span className="interviewCountryPill">{student.prospectAdmissionNumber || 'Admission ref pending'}</span>
                    <span className="interviewMetaTag">{student.course || 'Course not set'}</span>
                    <span className="interviewMetaTag">{invoiceCurrency}</span>
                    <span className="interviewMetaTag">{student.cohort || 'Cohort not set'}</span>
                  </div>

                  <div className="interviewApplicantMetaGrid">
                    <div className="interviewApplicantMetaCard">
                      <span>Interview completed</span>
                      <strong>{formatDateTime(student.interviewCompletedAt)}</strong>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>First invoice</span>
                      <strong>{student.firstPaymentInvoiceNumber ? `#${student.firstPaymentInvoiceNumber}` : 'Not sent yet'}</strong>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Invoice amount</span>
                      <strong>
                        {student.firstPaymentInvoiceAmount
                          ? formatCurrency(student.firstPaymentInvoiceAmount, invoiceCurrency)
                          : (student.courseFeeDeposit ? `Autofill: ${formatCurrency(student.courseFeeDeposit, invoiceCurrency)}` : 'Course deposit not set')}
                      </strong>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Due date</span>
                      <strong>{formatDateOnly(student.firstPaymentInvoiceDueDate)}</strong>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Phone</span>
                      <strong>
                        <MaskedPhone
                          phoneNumber={student.phoneNumber}
                          studentId={studentId}
                          studentName={student.bio}
                        />
                      </strong>
                    </div>
                    <div className="interviewApplicantMetaCard">
                      <span>Messages</span>
                      <strong>
                        Email: {student.firstPaymentInvoiceEmailStatus || 'not sent'}<br />
                        SMS: {student.firstPaymentInvoiceSmsStatus || 'not sent'}
                      </strong>
                    </div>
                  </div>

                  <div className="interviewApplicantActions">
                    <button
                      className="interviewCallBtn invoicingSendBtn"
                      onClick={() => openInvoiceModal(student)}
                    >
                      <FontAwesomeIcon icon={student.firstPaymentInvoiceStatus === 'sent' ? faEnvelope : faFileInvoice} />
                      <span>{student.firstPaymentInvoiceStatus === 'sent' ? 'Resend invoice' : 'Send invoice'}</span>
                    </button>
                    <button
                      className="appsViewBtn interviewCardViewBtn"
                      onClick={() => viewStudent(student)}
                    >
                      View applicant
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        show={showInvoiceModal}
        onHide={sendingInvoice ? undefined : closeInvoiceModal}
        centered
        dialogClassName="interviewScheduleModal"
      >
        <Modal.Header closeButton={!sendingInvoice}>
          <Modal.Title>Send first payment invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="interviewScheduleSummary">
            <p className="interviewScheduleKicker">Applicant</p>
            <h6>{selectedApplicant?.bio || 'Applicant'}</h6>
            <span>{selectedApplicant?.course || 'Course not set'} - {selectedApplicant?.invoiceCurrency || getInvoiceCurrency(selectedApplicant)} invoice</span>
          </div>

          <div className="interviewScheduleGrid">
            <Form.Group>
              <Form.Label>First payment amount</Form.Label>
              <Form.Control
                type="number"
                min="1"
                step={(selectedApplicant?.invoiceCurrency || getInvoiceCurrency(selectedApplicant)) === 'USD' ? '0.01' : '1'}
                placeholder="e.g. 20000"
                value={invoiceAmount}
                onChange={(event) => setInvoiceAmount(event.target.value)}
              />
              <Form.Text className="text-muted">
                {selectedApplicant?.courseFeeDeposit
                  ? `Autofilled from ${selectedApplicant?.course || 'course'} fee deposit: ${formatCurrency(selectedApplicant.courseFeeDeposit, selectedApplicant?.invoiceCurrency || getInvoiceCurrency(selectedApplicant))}`
                  : 'No fee deposit is set for this course yet.'}
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Due date</Form.Label>
              <Form.Control
                type="date"
                value={invoiceDueDate}
                onChange={(event) => setInvoiceDueDate(event.target.value)}
              />
            </Form.Group>
          </div>

          <div className="interviewSchedulePreview">
            <p className="interviewSchedulePreviewLabel">What will be sent</p>
            <div className="interviewSchedulePreviewRow">
              <span>Email</span>
              <strong>{selectedApplicant?.userEmail || 'No email saved'}</strong>
            </div>
            <div className="interviewSchedulePreviewRow">
              <span>SMS</span>
              <strong>{isKenyanSmsPhone(selectedApplicant?.phoneNumber) ? selectedApplicant?.phoneNumber : 'Skipped unless phone is a Kenyan SMS number'}</strong>
            </div>
            <div className="interviewSchedulePreviewRow">
              <span>Payment instructions</span>
              <strong>
                {(selectedApplicant?.invoiceCurrency || getInvoiceCurrency(selectedApplicant)) === 'USD'
                  ? 'Student account payment only'
                  : `Student account or Paybill ${FEE_PAYMENT_PAYBILL}, Account ${getApplicationRef(selectedApplicant) || 'Application Ref pending'}`}
              </strong>
            </div>
            <div className="interviewSchedulePreviewRow">
              <span>Invoice preview</span>
              <strong>{formatCurrency(invoiceAmount, selectedApplicant?.invoiceCurrency || getInvoiceCurrency(selectedApplicant))} due {invoiceDueDate ? formatDateOnly(new Date(`${invoiceDueDate}T23:59:00`)) : 'Not set'}</strong>
            </div>
            <div className="interviewSchedulePreviewRow">
              <span>Exchange rate</span>
              <strong>{selectedApplicant?.invoiceCurrency === 'USD' ? `1 USD = KES ${parseMoneyValue(selectedApplicant?.usdToKesRate || usdToKesRate).toLocaleString('en-KE')}` : 'Not needed for KES invoices'}</strong>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={closeInvoiceModal} disabled={sendingInvoice}>
            Cancel
          </Button>
          <Button variant="dark" onClick={handleSendInvoice} disabled={sendingInvoice}>
            {sendingInvoice ? 'Sending invoice...' : 'Send invoice'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default InvoicingComp;
